/**
 * SOLID Review
 * S — Strong. One concern: mint the request context every client port call carries first.
 * O — Strong. A new context field is one line.
 * L — N/A — no hierarchy.
 * I — Strong. One method.
 * D — Strong. The context's type and the clock it is handed (`IServiceViewClock`); it reads no clock of its own.
 */
import type { IContextRequest } from '../../application/ports/IContextRequest';
import type { IServiceViewClock } from '../viewmodels/platform/IConfigDepViewPlatform';

/** How long a use case may run before the kernel refuses a late call: generous, for two server reads. */
const USE_CASE_BUDGET_MS = 30_000;

/**
 * A FRESH CONTEXT PER USE-CASE CALL (frontend strict-ports plan, FE1e, 2026-10-03) — the client twin of the backend's
 * `RequestContexts`. The id is unique within the tab (a time prefix and a counter); the deadline is absolute, so a hop that adds
 * latency cannot restart it (`IPayloadCallContext`). The id is not sent to the server yet: joining client and server traces is
 * deferred (plan, "Deferred").
 *
 * An object the composition root builds once since frontend-framework-oo P1 (§28.293): its counter was module state, and its clock
 * `Date.now()` — both the tab's, now both a field.
 */
export class ClientRequests {
  private minted = 0;

  constructor(private readonly clock: IServiceViewClock) {}

  /** A context, `budgetMs` from now — shorter for a caller that stops waiting sooner. */
  context(budgetMs: number = USE_CASE_BUDGET_MS): IContextRequest {
    const now = this.clock.now();
    this.minted += 1;
    return { requestId: `client-${now.toString(36)}-${this.minted}`, deadlineAt: now + budgetMs };
  }
}
